import type { UseConversationsFilterReturn } from './index.types';
import { MobXProviderContext } from 'mobx-react';
import { useFetchConversations } from 'Modules/conversation/conversations-list/requests/index.requests';
import type { ConversationsFilterType } from 'Modules/conversation/conversations-list/store/index.types';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import { useIsSecondaryMenuVisible } from 'Modules/ui/secondaryMenu/hooks/useIsSecondaryMenuVisible';
import * as React from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import {
  getStoredConversationsFilter,
  setStoredConversationsFilter,
} from '../storage';

const FILTER_TITLES: Record<ConversationsFilterType, string> = {
  all: 'All messages',
  unread: 'Unread',
  group: 'Group',
  archived: 'Archived',
};

/**
 * Hook to manage the conversations list filter
 * Handles filter persistence per user, popover state, and filter change actions
 */
export const useConversationsFilter = (): UseConversationsFilterReturn => {
  const {
    person: { loggedInPersonId },
  } = React.useContext<RootStoreProps>(MobXProviderContext);
  const isSecondaryMenuVisible = useIsSecondaryMenuVisible();
  const { conversationsListFilter, setConversationsListFilter } =
    useConversationStore();
  const { refetch } = useFetchConversations();
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);

  // Close the filter popover when the conversations panel is hidden
  React.useEffect(() => {
    if (isPopoverOpen && !isSecondaryMenuVisible) {
      setIsPopoverOpen(false);
    }
  }, [isPopoverOpen, isSecondaryMenuVisible, setIsPopoverOpen]);

  // Load the persisted filter for the logged-in user on mount and on user change
  React.useEffect(() => {
    if (!loggedInPersonId || loggedInPersonId < 0) {
      return;
    }
    const stored = getStoredConversationsFilter(loggedInPersonId);
    setConversationsListFilter(stored);
    void refetch();
  }, [loggedInPersonId, setConversationsListFilter, refetch]);

  const options = React.useMemo(() => {
    const handleFilterChange = (filter: ConversationsFilterType) => {
      setConversationsListFilter(filter);
      setStoredConversationsFilter(loggedInPersonId, filter);
      pushToGTMDataLayer('chat_filter', { filter });
      void refetch();
    };

    return [
      {
        text: FILTER_TITLES['all'],
        isSelected: conversationsListFilter === 'all',
        onClick: () => handleFilterChange('all'),
      },
      {
        text: FILTER_TITLES['unread'],
        isSelected: conversationsListFilter === 'unread',
        onClick: () => handleFilterChange('unread'),
      },
      {
        text: FILTER_TITLES['group'],
        isSelected: conversationsListFilter === 'group',
        onClick: () => handleFilterChange('group'),
      },
      {
        text: FILTER_TITLES['archived'],
        isSelected: conversationsListFilter === 'archived',
        onClick: () => handleFilterChange('archived'),
      },
    ];
  }, [
    conversationsListFilter,
    refetch,
    setConversationsListFilter,
    loggedInPersonId,
  ]);

  return {
    options,
    conversationsListFilter,
    currentFilterLabel: FILTER_TITLES[conversationsListFilter],
    isPopoverOpen,
    setIsPopoverOpen,
  };
};
